import React, { useEffect, useRef, useState } from "react";
import LogoMark from "../../assets/images/logo.png";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiShield, FiActivity, FiLock, FiArrowLeft } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Loader from "../../components/Loader";
import "./login.css";

const Login = () => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [otpSecVisible, setOtpSecVisible] = useState(false); // Initially false for login form
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes timer
  const [isExpired, setIsExpired] = useState(false);

  const OTP_TIMER_KEY = "otp_timer_start";

  useEffect(() => {
    if (otpSecVisible) {
      const startTime = localStorage.getItem(OTP_TIMER_KEY);
      if (startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const remaining = 600 - elapsed;
        if (remaining > 0) {
          setTimeLeft(remaining);
          setIsExpired(false);
        } else {
          setTimeLeft(0);
          setIsExpired(true);
          toast.error("OTP expired! Please request a new code.");
          localStorage.removeItem(OTP_TIMER_KEY);
        }
      }
    }
  }, [otpSecVisible]);

  useEffect(() => {
    if (otpSecVisible && timeLeft > 0 && !isExpired) {
      const timerId = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsExpired(true);
            localStorage.removeItem(OTP_TIMER_KEY);
            toast.error("OTP expired! Please request a new code.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [otpSecVisible, timeLeft, isExpired]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSubmitLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Username and Password are required!");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/api/v1/admin/admin-login`,
        { username, password }
      );

      if (res.data.success) {
        setOtpSecVisible(true);
        localStorage.setItem(OTP_TIMER_KEY, Date.now());
        toast.success("OTP sent to your email!");
      } else {
        toast.error(res.data.message || "Login failed.");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    const newCode = [...code];

    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split("");
      for (let i = 0; i < 6; i++) {
        newCode[i] = pastedCode[i] || "";
      }
      setCode(newCode);

      const lastFilledIndex = newCode.findLastIndex((digit) => digit !== "");
      const focusIndex = lastFilledIndex < 5 ? lastFilledIndex + 1 : 5;
      inputRefs.current[focusIndex].focus();
    } else {
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newCode = pasteData.split("");
    setCode(newCode);
    inputRefs.current[newCode.length - 1]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalCode = code.join("");
    if (finalCode.length !== 6 || isExpired) {
      toast.error("Please enter a valid 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/api/v1/admin/admin-verify-login`,
        { code: finalCode }
      );

      if (res.data.success) {
        toast.success(res.data.message);
        localStorage.setItem("admintoken", res.data.token);
        localStorage.setItem(
          "adminauthor",
          JSON.stringify({ admin: res.data.subAdmin, token: res.data.token })
        );

        localStorage.removeItem(OTP_TIMER_KEY);
        navigate("/dashboard/admin");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!username) {
      toast.error("username is required.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/api/v1/admin/admin-login-otp-again`,
        { username }
      );
      toast.success("New OTP sent to your email!");
      setTimeLeft(600);
      setIsExpired(false);
      setCode(["", "", "", "", "", ""]);
      localStorage.setItem(OTP_TIMER_KEY, Date.now());
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pqs-auth">
      {loading && <Loader />}

      {/* Brand panel */}
      <aside className="pqs-auth__brand">
        <div className="pqs-auth__brand-top">
          <img className="pqs-auth__mark" src={LogoMark} alt="PQS" />
        </div>

        <div className="pqs-auth__brand-mid">
          <h1 className="pqs-auth__headline">
            Admin <span>control center</span> for Premier Quantitative Strategies
          </h1>
          <p className="pqs-auth__lead">
            Manage funds, subscriptions, deposits, withdrawals and investors from
            one secure dashboard.
          </p>
          <ul className="pqs-auth__features">
            <li><span className="pqs-auth__dot"><FiShield /></span> Bank-grade access with email OTP verification</li>
            <li><span className="pqs-auth__dot"><FiActivity /></span> Real-time transactions & activity monitoring</li>
            <li><span className="pqs-auth__dot"><FiLock /></span> Role-based controls for every admin action</li>
          </ul>
        </div>

        <div className="pqs-auth__brand-foot">
          © {new Date().getFullYear()} Premier Quantitative Strategies. All rights reserved.
        </div>
      </aside>

      {/* Form panel */}
      <main className="pqs-auth__panel">
        <div className="pqs-auth__card">
          <div className="pqs-auth__badge">
            <span className="pqs-auth__badge-mark">PQS</span>
            <span className="pqs-auth__badge-text">
              PQS Admin
              <small>Control Center</small>
            </span>
          </div>

          {otpSecVisible ? (
            <>
              <div className="pqs-auth__eyebrow">Two-step verification</div>
              <h2 className="pqs-auth__title">Enter your code</h2>
              <p className="pqs-auth__sub">
                We sent a 6-digit verification code to your email. Enter it below to continue.
              </p>

              <div className={`pqs-timer ${isExpired ? "expired" : ""}`}>
                <FiLock size={13} />
                {isExpired ? "Code expired" : `Expires in ${formatTime(timeLeft)}`}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="pqs-otp">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      value={digit}
                      onChange={(e) => handleChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={handlePaste}
                      className="pqs-otp-input"
                      maxLength="1"
                      disabled={isExpired}
                      inputMode="numeric"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  className="pqs-btn"
                  style={{ marginTop: 18 }}
                  disabled={code.includes("") || isExpired}
                >
                  {isExpired ? "Code expired" : "Verify & sign in"}
                </button>
              </form>

              <div className="pqs-row" style={{ marginTop: 18, marginBottom: 0 }}>
                <button type="button" className="pqs-back" onClick={() => setOtpSecVisible(false)}>
                  <FiArrowLeft style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Back to login
                </button>
                <button type="button" className="pqs-resend" onClick={handleResend}>
                  Resend code
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="pqs-auth__eyebrow">Welcome back</div>
              <h2 className="pqs-auth__title">Sign in to your dashboard</h2>
              <p className="pqs-auth__sub">Enter your credentials to access the admin panel.</p>

              <form onSubmit={handleSubmitLogin}>
                <div className="pqs-field">
                  <label className="pqs-label" htmlFor="username">Username</label>
                  <div className="pqs-input-wrap">
                    <input
                      id="username"
                      type="text"
                      className="pqs-input"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.trim())}
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="pqs-field">
                  <label className="pqs-label" htmlFor="password">Password</label>
                  <div className="pqs-input-wrap">
                    <input
                      id="password"
                      type={passwordVisible ? "text" : "password"}
                      className="pqs-input"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="pqs-eye"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? <FaEye /> : <FaEyeSlash />}
                    </button>
                  </div>
                </div>

                <div className="pqs-row">
                  <label className="pqs-check">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                    />
                    Remember me
                  </label>
                  <Link className="pqs-link" to="/forgot-password">
                    Forgot password?
                  </Link>
                </div>

                <button type="submit" className="pqs-btn">Sign in</button>
              </form>

              <p className="pqs-auth__foot">
                Trouble signing in?{" "}
                <Link className="pqs-link" to="/forgot-password">Get help</Link>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Login;
